/**
 * ArrayUpdater - Handles array field DOM updates (data-dr-list cloning)
 *
 * Ported from the current runtime's updateArrayField() function.
 * Finds data-dr-list containers, clones template data-dr-list-item elements,
 * fills in field values, and dispatches dr:content-updated events.
 */

export class ArrayUpdater {
  /**
   * Update an array field by cloning template items and filling in data.
   * Dispatches dr:content-updated after completion.
   */
  updateArrayField(container: Element, listName: string, items: any[], fallbackSectionId?: string): void {
    let listEls = container.querySelectorAll(`[data-dr-list="${listName}"]`);
    if (listEls.length === 0) return;

    listEls.forEach((listEl) => {
      const templateItem = listEl.querySelector('[data-dr-list-item]');
      if (!templateItem) return;

      const templateClone = templateItem.cloneNode(true) as HTMLElement;

      // Remove all existing items
      const existingItems = listEl.querySelectorAll('[data-dr-list-item]');
      existingItems.forEach((item) => item.remove());

      // Create new items from data
      for (const itemData of items) {
        const newItem = templateClone.cloneNode(true) as HTMLElement;

        // Primitive item (e.g. string array like amenities)
        if (typeof itemData !== 'object' || itemData === null) {
          const firstFieldEl = newItem.querySelector('[data-dr-field]') as HTMLElement | null;
          if (firstFieldEl) {
            firstFieldEl.textContent = String(itemData);
          }
          listEl.appendChild(newItem);
          continue;
        }

        // Object item — fill in field values
        for (const [key, value] of Object.entries(itemData)) {
          // Handle nested objects with dot-notation fields
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            for (const [subKey, subValue] of Object.entries(value)) {
              const subEl = newItem.querySelector(`[data-dr-field="${key}.${subKey}"]`) as HTMLElement | null;
              if (subEl) {
                subEl.textContent = String(subValue);
              }
            }
            continue;
          }

          const fieldEl = newItem.querySelector(`[data-dr-field="${key}"]`) as HTMLElement | null;
          if (!fieldEl) continue;

          const tagName = fieldEl.tagName.toLowerCase();
          if (tagName === 'img') {
            (fieldEl as HTMLImageElement).src = String(value);
          } else if (tagName === 'video') {
            (fieldEl as HTMLVideoElement).src = String(value);
          } else if (tagName === 'iframe') {
            (fieldEl as HTMLIFrameElement).src = String(value);
          } else if (tagName === 'path') {
            (fieldEl as unknown as SVGPathElement).setAttribute('d', String(value));
          } else if (tagName === 'a') {
            (fieldEl as HTMLAnchorElement).textContent = String(value);
            if ((itemData as any).href) {
              (fieldEl as HTMLAnchorElement).href = String((itemData as any).href);
            }
          } else {
            fieldEl.textContent = String(value);
          }
        }

        listEl.appendChild(newItem);
      }
    });

    // Fire dr:content-updated event so template scripts can re-initialize components
    const sectionEl = container.closest('[data-dr-section]');
    const sectionId = sectionEl?.getAttribute('data-dr-section') || fallbackSectionId || '';
    document.dispatchEvent(new CustomEvent('dr:content-updated', {
      detail: { sectionId, listName }
    }));
  }
}
